/**
 * Custom Satisfaction Rating Icons
 *
 * Designed to match the Have-Done design system - warm, friendly, and celebratory.
 * These custom icons replace generic emojis for a more polished, premium feel.
 */

import { type SVGProps } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  filled?: boolean;
}

/**
 * Level 1: Meh - Slightly disappointed, flat expression
 */
export function SatisfactionMeh({ filled = false, className = '', ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        </>
      ) : (
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      )}
      {/* Eyes - slightly droopy */}
      <circle cx="8.5" cy="10" r="1.25" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1.25" fill="currentColor" />
      {/* Mouth - slight frown */}
      <path
        d="M8.5 15.5C9.5 14.5 10.5 14 12 14C13.5 14 14.5 14.5 15.5 15.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Level 2: Okay - Neutral, content expression
 */
export function SatisfactionOkay({ filled = false, className = '', ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        </>
      ) : (
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      )}
      {/* Eyes - neutral */}
      <circle cx="8.5" cy="10" r="1.25" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1.25" fill="currentColor" />
      {/* Mouth - straight line */}
      <path
        d="M8 15H16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Level 3: Good - Pleasant, light smile
 */
export function SatisfactionGood({ filled = false, className = '', ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        </>
      ) : (
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      )}
      {/* Eyes - happy */}
      <circle cx="8.5" cy="10" r="1.25" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1.25" fill="currentColor" />
      {/* Mouth - gentle smile */}
      <path
        d="M8 14C9 15.5 10.5 16 12 16C13.5 16 15 15.5 16 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Level 4: Great - Happy, wide smile
 */
export function SatisfactionGreat({ filled = false, className = '', ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        </>
      ) : (
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      )}
      {/* Eyes - happy arcs */}
      <path
        d="M7 10C7.5 9 8 8.5 9 8.5C10 8.5 10.5 9 11 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M13 10C13.5 9 14 8.5 15 8.5C16 8.5 16.5 9 17 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      {/* Mouth - big smile */}
      <path
        d="M7 13.5C8.5 16 10 17 12 17C14 17 15.5 16 17 13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Level 5: Amazing - Ecstatic, celebration with sparkle
 */
export function SatisfactionAmazing({ filled = false, className = '', ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      {...props}
    >
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        </>
      ) : (
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
      )}
      {/* Sparkle decoration */}
      <path
        d="M19 3L19.5 4.5L21 5L19.5 5.5L19 7L18.5 5.5L17 5L18.5 4.5L19 3Z"
        fill="currentColor"
      />
      {/* Eyes - star eyes */}
      <path
        d="M8.5 10L9 8.5L9.5 10L11 10.5L9.5 11L9 12.5L8.5 11L7 10.5L8.5 10Z"
        fill="currentColor"
      />
      <path
        d="M14.5 10L15 8.5L15.5 10L17 10.5L15.5 11L15 12.5L14.5 11L13 10.5L14.5 10Z"
        fill="currentColor"
      />
      {/* Mouth - wide open smile */}
      <path
        d="M7 14C8.5 17 10 18 12 18C14 18 15.5 17 17 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M9 15.5C10 16.5 11 17 12 17C13 17 14 16.5 15 15.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Map of satisfaction values to their icon components
 */
export const SATISFACTION_ICONS = {
  1: SatisfactionMeh,
  2: SatisfactionOkay,
  3: SatisfactionGood,
  4: SatisfactionGreat,
  5: SatisfactionAmazing,
} as const;

export type SatisfactionValue = keyof typeof SATISFACTION_ICONS;
